import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";

type AttendeeCountRadioChartProps = {
  data: { participants: number; count: number }[];
};
const AttendeeCountRatioChart: React.FC<AttendeeCountRadioChartProps> = ({ data }) => {
  const [legends, setLegends] = useState<number[]>([]);
  const [series, setSeries] = useState<number[]>([]);
  const [smallLegends, setSmallLegends] = useState<number[]>([]);
  const [smallSeries, setSmallSeries] = useState<number[]>([]);
  const [averageParticipants, setAverageParticipants] = useState<number>();

  const chartOptions: ApexOptions = {
    labels: legends.map((item) => (item === -1 ? "기타" : item + "인")),
    series: series,
    colors: [
      "var(--global-color-blue-70",
      "var(--global-color-skyblue-70",
      "var(--global-color-cyan-70)",
      "var(--global-color-lime-70)",
      "var(--global-color-yellow-80)",
      "var(--global-color-orange-80)",
      "var(--global-color-purple-80)",
    ],

    chart: {
      type: "donut",
      animations: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
      toolbar: {
        tools: {
          download: true,
        },
        show: true,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
      },
    },

    dataLabels: {
      enabled: true,
      dropShadow: {
        enabled: false,
      },
    },
    stroke: {
      show: false,
      width: 0,
    },
    legend: {
      position: "right",
      fontSize: "12px",
      fontWeight: 400,
      offsetY: 150,

      itemMargin: {
        horizontal: 7,
      },
      markers: {
        fillColors: [],
      },
    },
    tooltip: {
      y: {
        formatter: function (value, { seriesIndex }) {
          const label = legends[seriesIndex];
          if (label === -1) {
            return `전체 회의 수: ${value} <br/><br/> ${smallLegends
              .slice(0, 4)
              .map((item, index) => `${item}인 : ${smallLegends[index]}`)
              .join("<br/>")}<br/>그 외: ${smallSeries.slice(4).reduce((acc, val) => acc + val, 0)}`;
          }
          return `${value}`;
        },
      },
    },
  };

  useEffect(() => {
    if (data) {
      //count 기준으로 정렬
      const sortedData = data.sort((a, b) => b.count - a.count);
      const participants = sortedData.map((item) => item.participants);
      const counts = sortedData.map((item) => item.count);

      //실제로 Pie차트의 섹터로써 보일 legend / value
      const bigParticipants = participants.slice(0, 6);
      const bigCounts = counts.slice(0, 6);

      //기타 섹터로 표현될 나머지 값들
      const smallParticipants = participants.slice(6);
      const smallCounts = counts.slice(6);

      //마지막은 기타 섹터로 넣기 (이때, -1은 "기타"로 처리합니다.)
      const etcCount = smallCounts.reduce((acc, val) => acc + val, 0);
      if (etcCount > 0) {
        bigParticipants.push(-1);
        bigCounts.push(smallCounts.reduce((acc, val) => acc + val, 0));
      }

      //전체 인원수 * 회의 수 / 총 회의 수 = 평균 참석 인원수
      const totalAttendanceCount =
        counts.map((item, index) => item * participants[index]).reduce((acc, val) => acc + val, 0) /
        counts.reduce((acc, val) => acc + val, 0);

      setAverageParticipants(totalAttendanceCount);
      setLegends(bigParticipants);
      setSeries(bigCounts);
      setSmallLegends(smallParticipants);
      setSmallSeries(smallCounts);
    }
  }, [data]);

  return (
    <Container>
      <TitleContainer>
        <UI.Typography type="body01 normal" fontStyle="bold" color="var(--alias-color-label-neutral)">
          참석 인원수별 회의 비율
        </UI.Typography>
        <UI.Typography type="caption01" color="var(--alias-color-label-alternative)">
          참석자 정보를 수집하지 않는 비공개 회의는 본 항목에 포함되지 않습니다.
        </UI.Typography>
      </TitleContainer>
      <ContentContainer>
        <TagContainer>
          <UI.Typography type="label01" color="var(--alias-color-label-normal)">
            평균 참석 인원수: <strong>{averageParticipants ? averageParticipants.toFixed(1) + "인" : "-"}</strong>
          </UI.Typography>
        </TagContainer>
        <Chart options={chartOptions} series={series} type="donut" width={380} height={290} />
      </ContentContainer>
    </Container>
  );
};

export default React.memo(AttendeeCountRatioChart);

const Container = styled.div`
  display: flex;
  width: 420px;
  height: 480px;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  box-sizing: border-box;

  border-radius: 12px;
  border: 1px solid var(--alias-color-line-normal);
  background: var(--alias-color-background-normal-normal);
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  flex: 1 0 0;
  align-self: stretch;
`;

const TagContainer = styled.div`
  display: flex;
  padding: 8px 12px;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-left: auto;

  border-radius: 8px;
  border: 1px solid var(--alias-color-line-normal);
  background: var(--alias-color-background-normal-normal);
`;
